<template>
  <div class="app-container">

    <Notifications />
    <template v-if="isLandingPage">
      <router-view />
    </template>

    <template v-else>
      <header class="header">
        <div class="header-content">
          <img src="https://cdn1.ferryhopper.com/img/ferryhopper-logo-small.svg" alt="Ferryhopper Logo"
            class="header-logo" />
          <span class="header-spacer"></span>
          <a href="#" class="logout-link" @click.prevent="handleLogout">Logout</a>
        </div>
      </header>
      <aside class="sidebar">

        <nav class="sidebar-menu">
          <router-link to="/dashboard/overview" exact class="sidebar-link" active-class="sidebar-link-active">
            <span class="icon"><i class="fa fa-bar-chart"></i></span>
            <span class="sidebar-label">Overview</span>
          </router-link>
          <router-link to="/dashboard/bookings-list" class="sidebar-link" active-class="sidebar-link-active">
            <span class="icon"><i class="fa fa-calendar"></i></span>
            <span class="sidebar-label">Bookings</span>
          </router-link>
          <router-link to="/dashboard/invoices" class="sidebar-link" active-class="sidebar-link-active"
            v-if="showInvoices">
            <span class="icon"><i class="fa fa-file-text-o"></i></span>
            <span class="sidebar-label">Invoices</span>
          </router-link>
          <router-link to="/dashboard/trips-list" class="sidebar-link" active-class="sidebar-link-active">
            <span class="icon"><i class="fa fa-list"></i></span>
            <span class="sidebar-label">Trips</span>
          </router-link>
          <router-link to="/dashboard/statistics" class="sidebar-link" active-class="sidebar-link-active">
            <span class="icon"><i class="fa fa-line-chart"></i></span>
            <span class="sidebar-label">Statistics</span>
          </router-link>
          <router-link to="/dashboard/widget" class="sidebar-link" active-class="sidebar-link-active">
            <span class="icon"><i class="fa fa-th-large"></i></span>
            <span class="sidebar-label">Search Widget</span>
          </router-link>
          <router-link to="/dashboard/embeddable-widget" class="sidebar-link" active-class="sidebar-link-active">
            <span class="icon"><i class="fa fa-code"></i></span>
            <span class="sidebar-label">Trips Widget</span>
          </router-link>
          <router-link to="/dashboard/wallet" class="sidebar-link" active-class="sidebar-link-active">
            <span class="icon"><i class="fa fa-credit-card"></i></span>
            <span class="sidebar-label">Wallet</span>
          </router-link>
          <router-link to="/dashboard/affiliates/create" class="sidebar-link" active-class="sidebar-link-active"
            v-if="showCreateUser">
            <span class="icon"><i class="fa fa-user"></i></span>
            <span class="sidebar-label">New User</span>
          </router-link>
          <router-link to="/dashboard/affiliates" class="sidebar-link" active-class="sidebar-link-active"
            v-if="showUsersList">
            <span class="icon"><i class="fa fa-users"></i></span>
            <span class="sidebar-label">Affiliates</span>
          </router-link>
        </nav>
      </aside>
      <div class="main-wrapper">
        <main>
          <router-view />
        </main>
      </div>
    </template>

  </div>
</template>

<script>

import Notifications from '@/components/UIComponents/Notifications.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'App',
  components: {
    Notifications
  },
  async created() {
    try {
      await this.$store.dispatch('authentication/validateActiveSession')
    } catch (exception) {
      console.log('Started app without active session', exception)
    }
  },
  computed: {
    ...mapGetters({
      getAppUser: 'authentication/getAppUser',
      getAllowedActions: 'authentication/getAllowedActions',
      isImpersonating: 'authentication/isImpersonating'
    }),
    isLandingPage() {
      return !this.$route.path.includes('dashboard');
    },
    showCreateUser() {
      return this.getAllowedActions.includes('CREATE_USER');
    },
    showInvoices() {
      // allowedActions describes the logged-in admin, not the affiliate being
      // viewed, and it is never recomputed when "view as" starts. So in
      // impersonation mode we always offer the link and let the API decide:
      // it resolves the impersonated affiliate and answers 403 if that partner
      // has no invoice access, which surfaces as the page's error state.
      return this.getAllowedActions.includes('INVOICES') || this.isImpersonating;
    },
    showUsersList() {
      return this.getAllowedActions.includes('LIST_USERS');
    }
  },
  methods: {
    handleLogout() {
      this.$store.dispatch('authentication/logOutAction').then(() => {
        this.$router.push({ name: 'home' });
      });
    }
  }
}
</script>


<style>
:root {
  /* Grays */
  --gray-base: #000;
  --gray-darker: #212121;
  --gray-dark: #464646;
  --gray: #999999;
  --gray-d4: #d4d4d4;
  --gray-light: #ededed;
  --gray-lighter: #f7f7f7;
  --white: #fff;

  /* Brand Primary */
  --brand-primary-darker: #035246;
  --brand-primary-dark: #008f79;
  --brand-primary: #01c3a7;
  --brand-primary-light: #96ebdf;
  --brand-primary-extra-light: #e0faf6;
  --brand-success: #10b690;
  --brand-warning: #d40027;

  /* Purple */
  --purple: #91288c;
  --purple-dark: #642d91;
  --purple-light: #ddd3e6;
  --purple-extra-light: #f8f2f8;
  --purple-xxtra-light: #fbf6fa;

  /* Secondary/Pink */
  --brand-secondary: #ff5757;
  --brand-secondary-dark: #8f0e0e;
  --secondary-light: #ffc1c1;
  --secondary-extra-light: #fff0f0;

  /* Opacity Variations */
  --brand-primary-40: #01c3a766;
  --brand-primary-18: #01c3a72e;
  --brand-primary-50: #e0faf680;
  --brand-primary-30: #e0faf64d;
  --purple-30: #ddd3e64d;
  --secondary-extra-light-50: #fff0f080;
}

.app-container {
  font-family: 'Averta', Avenir, Helvetica, Arial, sans-serif;
  color: var(--gray-darker);
  background: var(--gray-lighter);
  min-height: 100vh;
  display: flex;
}

.header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 64px;
  background: #fff;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px var(--gray-light);
  z-index: 100;
}

.header-content {
  width: 100%;
  display: flex;
  align-items: center;
  height: 100%;
  padding-left: 2rem;
  padding-right: 2rem;
}

.header-spacer {
  flex: 1 1 auto;
}

.logout-link {
  color: var(--brand-primary);
  font-weight: 600;
  text-decoration: none;
  margin-left: 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: color 0.15s;
}

.logout-link:hover {
  color: var(--brand-primary-dark);
  text-decoration: underline;
}

.header-logo {
  height: 24px;
  display: block;
}

.sidebar {
  width: 220px;
  position: fixed;
  top: 64px;
  left: 0;
  height: calc(100vh - 64px);
  padding: 0 10px;
  background: #fff;
  box-shadow: 0 0 8px 0 rgba(45, 59, 78, 0.1);
  z-index: 99;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.sidebar-menu {
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
}

.sidebar-link {
  display: flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  color: var(--gray-dark);
  font-size: 1rem;
  text-decoration: none;
  border-radius: 6px;
  margin-bottom: 0.3rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: color 0.15s;
}

.sidebar-link:hover {
  background: var(--brand-primary-extra-light);
  color: var(--brand-primary);
}

.sidebar-label {
  margin-left: 1.1rem;
  font-size: 1rem;
  letter-spacing: 0;
  font-weight: 500;
}

.sidebar-menu .icon {
  font-size: 1.35rem;
  width: 28px;
  text-align: center;
  color: var(--gray-dark);
  flex-shrink: 0;
  transition: color 0.15s;
}

.sidebar-link:hover .icon {
  color: var(--brand-primary);
}

.sidebar-link-active {
  background: var(--brand-primary-extra-light);
  color: var(--brand-primary);
}

.sidebar-link-active .icon {
  color: var(--brand-primary);
}

.main-wrapper {
  margin-left: 220px;
  margin-top: 64px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
}

main {
  flex: 1;
  padding: 2rem;
  background: #f5f6fa;
  min-height: calc(100vh - 64px);
  min-height: calc(100vh - 56px);
}

@media (max-width: 800px) {
  .sidebar {
    width: 64px;
    min-width: 64px;
    align-items: center;
  }

  .sidebar-title {
    display: none;
  }

  .sidebar nav a {
    padding: 0.5rem;
    font-size: 0.9rem;
    text-align: center;
  }

  .sidebar nav a span.icon {
    margin-right: 0;
  }

  .main-wrapper {
    margin-left: 64px;
  }
}

@media (max-width: 900px) {
  .sidebar-label {
    display: none !important;
  }

  .sidebar {
    width: 64px;
    min-width: 64px;
    padding: 0 2px;
  }

  .sidebar-link {
    justify-content: center;
    padding: 0.8rem 0.5rem;
  }
}
</style>
